import React, { useState, useEffect } from 'react';
import { FaCalendarAlt, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const CustomDatePicker = ({ value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('days'); // 'days' | 'months' | 'years'
  const [currentDate, setCurrentDate] = useState(() => {
    if (value) {
      const parsed = new Date(value);
      if (!isNaN(parsed)) return parsed;
    }
    return new Date();
  });
  const [yearPage, setYearPage] = useState(() => Math.floor((currentDate.getFullYear()) / 12) * 12);

  // Popup quick input states
  const [popupDay, setPopupDay] = useState(() => value ? (new Date(value).getDate() || "") : "");
  const [popupMonth, setPopupMonth] = useState(() => value ? ((new Date(value).getMonth() + 1) || "") : "");
  const [popupYear, setPopupYear] = useState(() => value ? (new Date(value).getFullYear() || "") : "");

  useEffect(() => {
    if (value) {
      const parsed = new Date(value);
      if (!isNaN(parsed)) {
        setCurrentDate(parsed);
        setYearPage(Math.floor(parsed.getFullYear() / 12) * 12);
        setPopupDay(parsed.getDate() || "");
        setPopupMonth((parsed.getMonth() + 1) || "");
        setPopupYear(parsed.getFullYear() || "");
      }
    } else {
      setPopupDay("");
      setPopupMonth("");
      setPopupYear("");
    }
  }, [value]);

  const handleQuickChange = (type, val) => {
    if (type === 'day') setPopupDay(val);
    if (type === 'month') setPopupMonth(val);
    if (type === 'year') setPopupYear(val);

    const newDay = type === 'day' ? val : popupDay;
    const newMonth = type === 'month' ? val : popupMonth;
    const newYear = type === 'year' ? val : popupYear;

    const parsedYear = parseInt(newYear, 10);
    const parsedMonth = parseInt(newMonth, 10);
    const parsedDay = parseInt(newDay, 10);

    if (!isNaN(parsedYear) && parsedYear > 1000 && !isNaN(parsedMonth) && parsedMonth >= 1 && parsedMonth <= 12 && !isNaN(parsedDay) && parsedDay >= 1 && parsedDay <= 31) {
      const newDateObj = new Date(parsedYear, parsedMonth - 1, parsedDay);
      if (!isNaN(newDateObj)) {
        setCurrentDate(newDateObj);
        setYearPage(Math.floor(parsedYear / 12) * 12);
        const formattedMonth = String(parsedMonth).padStart(2, '0');
        const formattedDay = String(parsedDay).padStart(2, '0');
        onChange(`${parsedYear}-${formattedMonth}-${formattedDay}`);
      }
    } else if (!isNaN(parsedYear) && parsedYear > 1000 && !isNaN(parsedMonth) && parsedMonth >= 1 && parsedMonth <= 12) {
      setCurrentDate(new Date(parsedYear, parsedMonth - 1, 1));
      setYearPage(Math.floor(parsedYear / 12) * 12);
    }
  };

  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  
  let startOffset = firstDayOfMonth.getDay() - 1;
  if (startOffset < 0) startOffset = 6;

  const handlePrevMonth = (e) => {
    e.stopPropagation();
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const handleNextMonth = (e) => {
    e.stopPropagation();
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const handleSelectDate = (day, e) => {
    e.stopPropagation();
    const formattedMonth = String(month + 1).padStart(2, '0');
    const formattedDay = String(day).padStart(2, '0');
    const selectedDateStr = `${year}-${formattedMonth}-${formattedDay}`;
    onChange(selectedDateStr);
    setIsOpen(false);
    setView('days');
  };

  let selectedYear = null, selectedMonth = null, selectedDay = null;
  if (value) {
    const parsed = new Date(value);
    if (!isNaN(parsed)) {
      selectedYear = parsed.getFullYear();
      selectedMonth = parsed.getMonth();
      selectedDay = parsed.getDate();
    }
  }

  return (
    <div className="custom-datepicker-container" style={{ position: 'relative', width: '100%' }}>
      <div 
        className="custom-datepicker-input"
        onClick={() => { setIsOpen(!isOpen); setView('days'); }}
        style={{ position: 'relative' }}
      >
        <input 
          type="text" 
          readOnly
          value={value || ""} 
          placeholder={placeholder || "YYYY-MM-DD"} 
          style={{ cursor: 'pointer', background: '#ffffff', width: '100%', height: '44px', padding: '0 16px', borderRadius: '8px', border: '1px solid #E2E6EF', fontWeight: '500', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box' }}
        />
        <FaCalendarAlt 
          style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', color: '#604BE8', pointerEvents: 'none' }} 
        />
      </div>

      {isOpen && (
        <>
          <div className="custom-datepicker-backdrop" onClick={() => setIsOpen(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }} />
          <div className="custom-datepicker-popup" style={{ position: 'absolute', top: 'calc(100% + 8px)', left: 0, width: '320px', background: '#ffffff', borderRadius: '20px', boxShadow: '0 10px 30px rgba(0,0,0,0.15)', padding: '24px', zIndex: 1001, border: '1px solid #e2e8f0', animation: 'slideDown 0.2s ease', boxSizing: 'border-box' }}>
            
            {/* Quick Input Row inside Popup */}
            <div className="popup-quick-inputs-container" style={{ display: 'flex', flexDirection: 'column', gap: '6px', margin: '0 0 24px 0', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Quick Enter Date</span>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Day</span>
                  <input 
                    type="number" 
                    value={popupDay} 
                    onChange={(e) => handleQuickChange('day', e.target.value)} 
                    placeholder="DD" 
                    min="1" 
                    max="31"
                    style={{ width: '100%', height: '38px', padding: '0 12px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box', textAlign: 'center', outline: 'none' }} 
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Month</span>
                  <input 
                    type="number" 
                    value={popupMonth} 
                    onChange={(e) => handleQuickChange('month', e.target.value)} 
                    placeholder="MM" 
                    min="1" 
                    max="12"
                    style={{ width: '100%', height: '38px', padding: '0 12px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box', textAlign: 'center', outline: 'none' }} 
                  />
                </div>
                <div style={{ flex: 1.5 }}>
                  <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Year</span>
                  <input 
                    type="number" 
                    value={popupYear} 
                    onChange={(e) => handleQuickChange('year', e.target.value)} 
                    placeholder="YYYY" 
                    min="1000" 
                    max="9999"
                    style={{ width: '100%', height: '38px', padding: '0 12px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box', textAlign: 'center', outline: 'none' }} 
                  />
                </div>
              </div>
            </div>

            {view === 'days' && (
              <>
                <div className="custom-datepicker-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 24px 0' }}>
                  <button type="button" onClick={handlePrevMonth} className="cal-nav-btn" style={{ width: '36px', height: '36px', background: '#F3F6FB', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px', transition: 'all 0.2s ease' }}>
                    <FaChevronLeft />
                  </button>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span onClick={(e) => { e.stopPropagation(); setView('months'); }} style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px', transition: 'background 0.2s' }} onMouseEnter={(e) => e.target.style.background = '#F3F6FB'} onMouseLeave={(e) => e.target.style.background = 'transparent'}>
                      {monthNames[month]}
                    </span>
                    <span onClick={(e) => { e.stopPropagation(); setYearPage(Math.floor(year / 12) * 12); setView('years'); }} style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px', transition: 'background 0.2s' }} onMouseEnter={(e) => e.target.style.background = '#F3F6FB'} onMouseLeave={(e) => e.target.style.background = 'transparent'}>
                      {year}
                    </span>
                  </div>
                  <button type="button" onClick={handleNextMonth} className="cal-nav-btn" style={{ width: '36px', height: '36px', background: 'transparent', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px', transition: 'all 0.2s ease' }}>
                    <FaChevronRight />
                  </button>
                </div>

                <div className="custom-datepicker-weekdays" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center', marginBottom: '16px' }}>
                  {["Mo", "Tu", "We", "Th", "Fri", "Sa", "Su"].map((w, i) => (
                    <span key={i} style={{ fontSize: '13px', fontWeight: '700', color: '#1B1B29' }}>{w}</span>
                  ))}
                </div>

                <div className="custom-datepicker-days" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px 4px' }}>
                  {Array.from({ length: startOffset }).map((_, i) => (
                    <div key={`empty-${i}`} />
                  ))}
                  {Array.from({ length: daysInMonth }).map((_, i) => {
                    const day = i + 1;
                    const isSelected = selectedYear === year && selectedMonth === month && selectedDay === day;

                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={(e) => handleSelectDate(day, e)}
                        style={{
                          height: '36px',
                          width: '100%',
                          border: 'none',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          fontWeight: isSelected ? '700' : '600',
                          cursor: 'pointer',
                          background: isSelected ? '#604BE8' : 'transparent',
                          color: isSelected ? '#ffffff' : '#1B1B29',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {view === 'months' && (
              <div>
                <div className="custom-datepicker-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 24px 0' }}>
                  <button type="button" onClick={(e) => { e.stopPropagation(); setView('days'); }} style={{ width: '36px', height: '36px', background: '#F3F6FB', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px' }}>
                    <FaChevronLeft />
                  </button>
                  <span style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29' }}>Select Month</span>
                  <div style={{ width: '36px' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  {monthNames.map((mName, index) => (
                    <button
                      key={mName}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentDate(new Date(year, index, 1));
                        setView('days');
                      }}
                      style={{
                        height: '42px',
                        border: 'none',
                        borderRadius: '10px',
                        background: index === month ? '#604BE8' : '#F3F6FB',
                        color: index === month ? '#ffffff' : '#1B1B29',
                        fontWeight: '600',
                        fontSize: '14px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {mName.substring(0, 3)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {view === 'years' && (
              <div>
                <div className="custom-datepicker-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 24px 0' }}>
                  <button type="button" onClick={(e) => { e.stopPropagation(); setYearPage(yearPage - 12); }} style={{ width: '36px', height: '36px', background: '#F3F6FB', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px' }}>
                    <FaChevronLeft />
                  </button>
                  <span style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setView('days'); }}>
                    {yearPage} - {yearPage + 11}
                  </span>
                  <button type="button" onClick={(e) => { e.stopPropagation(); setYearPage(yearPage + 12); }} style={{ width: '36px', height: '36px', background: 'transparent', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px' }}>
                    <FaChevronRight />
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  {Array.from({ length: 12 }).map((_, i) => {
                    const y = yearPage + i;
                    return (
                      <button
                        key={y}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentDate(new Date(y, month, 1));
                          setView('days');
                        }}
                        style={{
                          height: '42px',
                          border: 'none',
                          borderRadius: '10px',
                          background: y === year ? '#604BE8' : '#F3F6FB',
                          color: y === year ? '#ffffff' : '#1B1B29',
                          fontWeight: '600',
                          fontSize: '14px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {y}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// DateRangePickerModal Component (Premium Dual Interactive Calendar with Quick Enter)
const DateRangePickerModal = ({ initialStart, initialEnd, onSave, onCancel }) => {
  const parseDate = (str) => {
    if (!str || str === '0') return new Date();
    if (str.includes('/')) {
      const parts = str.split('/');
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const year = parseInt(parts[2], 10);
      const d = new Date(year, month, day);
      return !isNaN(d) ? d : new Date();
    }
    const d = new Date(str);
    return !isNaN(d) ? d : new Date();
  };

  const formatDate = (dateObj) => {
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const [startDateObj, setStartDateObj] = useState(() => parseDate(initialStart));
  const [endDateObj, setEndDateObj] = useState(() => parseDate(initialEnd));

  const [startView, setStartView] = useState('days');
  const [endView, setEndView] = useState('days');

  const [startYearPage, setStartYearPage] = useState(() => Math.floor(startDateObj.getFullYear() / 12) * 12);
  const [endYearPage, setEndYearPage] = useState(() => Math.floor(endDateObj.getFullYear() / 12) * 12);

  const [startDayInput, setStartDayInput] = useState(() => startDateObj.getDate() || "");
  const [startMonthInput, setStartMonthInput] = useState(() => (startDateObj.getMonth() + 1) || "");
  const [startYearInput, setStartYearInput] = useState(() => startDateObj.getFullYear() || "");

  const [endDayInput, setEndDayInput] = useState(() => endDateObj.getDate() || "");
  const [endMonthInput, setEndMonthInput] = useState(() => (endDateObj.getMonth() + 1) || "");
  const [endYearInput, setEndYearInput] = useState(() => endDateObj.getFullYear() || "");

  const handleStartQuickChange = (type, val) => {
    if (type === 'day') setStartDayInput(val);
    if (type === 'month') setStartMonthInput(val);
    if (type === 'year') setStartYearInput(val);

    const newDay = type === 'day' ? val : startDayInput;
    const newMonth = type === 'month' ? val : startMonthInput;
    const newYear = type === 'year' ? val : startYearInput;

    const py = parseInt(newYear, 10);
    const pm = parseInt(newMonth, 10);
    const pd = parseInt(newDay, 10);

    if (!isNaN(py) && py > 1000 && !isNaN(pm) && pm >= 1 && pm <= 12 && !isNaN(pd) && pd >= 1 && pd <= 31) {
      const d = new Date(py, pm - 1, pd);
      if (!isNaN(d)) {
        setStartDateObj(d);
        setStartYearPage(Math.floor(py / 12) * 12);
      }
    } else if (!isNaN(py) && py > 1000 && !isNaN(pm) && pm >= 1 && pm <= 12) {
      setStartDateObj(new Date(py, pm - 1, 1));
      setStartYearPage(Math.floor(py / 12) * 12);
    }
  };

  const handleEndQuickChange = (type, val) => {
    if (type === 'day') setEndDayInput(val);
    if (type === 'month') setEndMonthInput(val);
    if (type === 'year') setEndYearInput(val);

    const newDay = type === 'day' ? val : endDayInput;
    const newMonth = type === 'month' ? val : endMonthInput;
    const newYear = type === 'year' ? val : endYearInput;

    const py = parseInt(newYear, 10);
    const pm = parseInt(newMonth, 10);
    const pd = parseInt(newDay, 10);

    if (!isNaN(py) && py > 1000 && !isNaN(pm) && pm >= 1 && pm <= 12 && !isNaN(pd) && pd >= 1 && pd <= 31) {
      const d = new Date(py, pm - 1, pd);
      if (!isNaN(d)) {
        setEndDateObj(d);
        setEndYearPage(Math.floor(py / 12) * 12);
      }
    } else if (!isNaN(py) && py > 1000 && !isNaN(pm) && pm >= 1 && pm <= 12) {
      setEndDateObj(new Date(py, pm - 1, 1));
      setEndYearPage(Math.floor(py / 12) * 12);
    }
  };

  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  const startYear = startDateObj.getFullYear();
  const startMonth = startDateObj.getMonth();
  const startFirstDay = new Date(startYear, startMonth, 1);
  const startDaysInMonth = new Date(startYear, startMonth + 1, 0).getDate();
  let startOffset = startFirstDay.getDay() - 1;
  if (startOffset < 0) startOffset = 6;

  const endYear = endDateObj.getFullYear();
  const endMonth = endDateObj.getMonth();
  const endFirstDay = new Date(endYear, endMonth, 1);
  const endDaysInMonth = new Date(endYear, endMonth + 1, 0).getDate();
  let endOffset = endFirstDay.getDay() - 1;
  if (endOffset < 0) endOffset = 6;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      backgroundColor: 'rgba(27, 27, 41, 0.5)', display: 'flex', justifyContent: 'center',
      alignItems: 'center', zIndex: 10005, backdropFilter: 'blur(8px)'
    }}>
      <div style={{
        backgroundColor: '#ffffff', borderRadius: '24px', width: '840px', maxWidth: '95%',
        padding: '36px', boxShadow: '0 25px 60px rgba(0, 0, 0, 0.2)',
        animation: 'fadeInScale 0.3s ease-out', fontFamily: "'Gilroy', 'Outfit', sans-serif",
        color: '#1B1B29', boxSizing: 'border-box'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px', borderBottom: '1px solid #F1F3F8', paddingBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#1B1B29' }}>Select Date Range</h3>
          <button onClick={onCancel} style={{ background: '#F1F4F9', border: 'none', width: '32px', height: '32px', borderRadius: '8px', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', color: '#1B1B29', transition: 'all 0.2s' }}><FaTimes /></button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginBottom: '36px' }}>
          {/* FROM DATE PICKER */}
          <div>
            <div style={{ marginBottom: '16px', paddingBottom: '12px', borderBottom: '2px solid #604BE8' }}>
              <span style={{ fontSize: '14px', fontWeight: '700', color: '#604BE8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>From Date</span>
            </div>

            {/* Quick Input Row */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Quick Enter Date</span>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Day</span>
                  <input type="number" value={startDayInput} onChange={(e) => handleStartQuickChange('day', e.target.value)} placeholder="DD" min="1" max="31" style={{ width: '100%', height: '38px', padding: '0 12px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box', textAlign: 'center', outline: 'none' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Month</span>
                  <input type="number" value={startMonthInput} onChange={(e) => handleStartQuickChange('month', e.target.value)} placeholder="MM" min="1" max="12" style={{ width: '100%', height: '38px', padding: '0 12px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box', textAlign: 'center', outline: 'none' }} />
                </div>
                <div style={{ flex: 1.5 }}>
                  <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Year</span>
                  <input type="number" value={startYearInput} onChange={(e) => handleStartQuickChange('year', e.target.value)} placeholder="YYYY" min="1000" max="9999" style={{ width: '100%', height: '38px', padding: '0 12px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box', textAlign: 'center', outline: 'none' }} />
                </div>
              </div>
            </div>

            {startView === 'days' && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <button type="button" onClick={() => setStartDateObj(new Date(startYear, startMonth - 1, 1))} style={{ width: '34px', height: '34px', background: '#F3F6FB', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px' }}><FaChevronLeft /></button>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span onClick={() => setStartView('months')} style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px' }} onMouseEnter={(e) => e.target.style.background = '#F3F6FB'} onMouseLeave={(e) => e.target.style.background = 'transparent'}>{monthNames[startMonth]}</span>
                    <span onClick={() => { setStartYearPage(Math.floor(startYear / 12) * 12); setStartView('years'); }} style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px' }} onMouseEnter={(e) => e.target.style.background = '#F3F6FB'} onMouseLeave={(e) => e.target.style.background = 'transparent'}>{startYear}</span>
                  </div>
                  <button type="button" onClick={() => setStartDateObj(new Date(startYear, startMonth + 1, 1))} style={{ width: '34px', height: '34px', background: 'transparent', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px' }}><FaChevronRight /></button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center', marginBottom: '14px' }}>
                  {["Mo", "Tu", "We", "Th", "Fri", "Sa", "Su"].map((w, i) => (<span key={i} style={{ fontSize: '13px', fontWeight: '700', color: '#1B1B29' }}>{w}</span>))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px 4px' }}>
                  {Array.from({ length: startOffset }).map((_, i) => (<div key={`empty-${i}`} />))}
                  {Array.from({ length: startDaysInMonth }).map((_, i) => {
                    const day = i + 1;
                    const isSelected = startDateObj.getDate() === day;
                    return (
                      <button key={day} type="button" onClick={() => { const d = new Date(startYear, startMonth, day); setStartDateObj(d); setStartDayInput(day); setStartMonthInput(startMonth + 1); setStartYearInput(startYear); }} style={{ height: '36px', width: '100%', border: 'none', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: isSelected ? '700' : '600', cursor: 'pointer', background: isSelected ? '#604BE8' : 'transparent', color: isSelected ? '#ffffff' : '#1B1B29', transition: 'all 0.2s ease' }}>
                        {day}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {startView === 'months' && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <button type="button" onClick={() => setStartView('days')} style={{ width: '34px', height: '34px', background: '#F3F6FB', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px' }}><FaChevronLeft /></button>
                  <span style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29' }}>Select Month</span>
                  <div style={{ width: '34px' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  {monthNames.map((mName, index) => (
                    <button key={mName} type="button" onClick={() => { setStartDateObj(new Date(startYear, index, startDateObj.getDate())); setStartMonthInput(index + 1); setStartView('days'); }} style={{ height: '42px', border: 'none', borderRadius: '10px', background: index === startMonth ? '#604BE8' : '#F3F6FB', color: index === startMonth ? '#ffffff' : '#1B1B29', fontWeight: '600', fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s ease' }}>
                      {mName.substring(0, 3)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {startView === 'years' && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <button type="button" onClick={() => setStartYearPage(startYearPage - 12)} style={{ width: '34px', height: '34px', background: '#F3F6FB', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px' }}><FaChevronLeft /></button>
                  <span style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29', cursor: 'pointer' }} onClick={() => setStartView('days')}>{startYearPage} - {startYearPage + 11}</span>
                  <button type="button" onClick={() => setStartYearPage(startYearPage + 12)} style={{ width: '34px', height: '34px', background: 'transparent', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px' }}><FaChevronRight /></button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  {Array.from({ length: 12 }).map((_, i) => {
                    const y = startYearPage + i;
                    return (
                      <button key={y} type="button" onClick={() => { setStartDateObj(new Date(y, startMonth, startDateObj.getDate())); setStartYearInput(y); setStartView('days'); }} style={{ height: '42px', border: 'none', borderRadius: '10px', background: y === startYear ? '#604BE8' : '#F3F6FB', color: y === startYear ? '#ffffff' : '#1B1B29', fontWeight: '600', fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s ease' }}>
                        {y}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* TO DATE PICKER */}
          <div>
            <div style={{ marginBottom: '16px', paddingBottom: '12px', borderBottom: '2px solid #06D6A0' }}>
              <span style={{ fontSize: '14px', fontWeight: '700', color: '#06D6A0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>To Date</span>
            </div>

            {/* Quick Input Row */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Quick Enter Date</span>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Day</span>
                  <input type="number" value={endDayInput} onChange={(e) => handleEndQuickChange('day', e.target.value)} placeholder="DD" min="1" max="31" style={{ width: '100%', height: '38px', padding: '0 12px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box', textAlign: 'center', outline: 'none' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Month</span>
                  <input type="number" value={endMonthInput} onChange={(e) => handleEndQuickChange('month', e.target.value)} placeholder="MM" min="1" max="12" style={{ width: '100%', height: '38px', padding: '0 12px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box', textAlign: 'center', outline: 'none' }} />
                </div>
                <div style={{ flex: 1.5 }}>
                  <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Year</span>
                  <input type="number" value={endYearInput} onChange={(e) => handleEndQuickChange('year', e.target.value)} placeholder="YYYY" min="1000" max="9999" style={{ width: '100%', height: '38px', padding: '0 12px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box', textAlign: 'center', outline: 'none' }} />
                </div>
              </div>
            </div>

            {endView === 'days' && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <button type="button" onClick={() => setEndDateObj(new Date(endYear, endMonth - 1, 1))} style={{ width: '34px', height: '34px', background: '#F3F6FB', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px' }}><FaChevronLeft /></button>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span onClick={() => setEndView('months')} style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px' }} onMouseEnter={(e) => e.target.style.background = '#F3F6FB'} onMouseLeave={(e) => e.target.style.background = 'transparent'}>{monthNames[endMonth]}</span>
                    <span onClick={() => { setEndYearPage(Math.floor(endYear / 12) * 12); setEndView('years'); }} style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px' }} onMouseEnter={(e) => e.target.style.background = '#F3F6FB'} onMouseLeave={(e) => e.target.style.background = 'transparent'}>{endYear}</span>
                  </div>
                  <button type="button" onClick={() => setEndDateObj(new Date(endYear, endMonth + 1, 1))} style={{ width: '34px', height: '34px', background: 'transparent', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px' }}><FaChevronRight /></button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center', marginBottom: '14px' }}>
                  {["Mo", "Tu", "We", "Th", "Fri", "Sa", "Su"].map((w, i) => (<span key={i} style={{ fontSize: '13px', fontWeight: '700', color: '#1B1B29' }}>{w}</span>))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px 4px' }}>
                  {Array.from({ length: endOffset }).map((_, i) => (<div key={`empty-${i}`} />))}
                  {Array.from({ length: endDaysInMonth }).map((_, i) => {
                    const day = i + 1;
                    const isSelected = endDateObj.getDate() === day;
                    return (
                      <button key={day} type="button" onClick={() => { const d = new Date(endYear, endMonth, day); setEndDateObj(d); setEndDayInput(day); setEndMonthInput(endMonth + 1); setEndYearInput(endYear); }} style={{ height: '36px', width: '100%', border: 'none', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: isSelected ? '700' : '600', cursor: 'pointer', background: isSelected ? '#06D6A0' : 'transparent', color: isSelected ? '#ffffff' : '#1B1B29', transition: 'all 0.2s ease' }}>
                        {day}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {endView === 'months' && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <button type="button" onClick={() => setEndView('days')} style={{ width: '34px', height: '34px', background: '#F3F6FB', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px' }}><FaChevronLeft /></button>
                  <span style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29' }}>Select Month</span>
                  <div style={{ width: '34px' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  {monthNames.map((mName, index) => (
                    <button key={mName} type="button" onClick={() => { setEndDateObj(new Date(endYear, index, endDateObj.getDate())); setEndMonthInput(index + 1); setEndView('days'); }} style={{ height: '42px', border: 'none', borderRadius: '10px', background: index === endMonth ? '#06D6A0' : '#F3F6FB', color: index === endMonth ? '#ffffff' : '#1B1B29', fontWeight: '600', fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s ease' }}>
                      {mName.substring(0, 3)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {endView === 'years' && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <button type="button" onClick={() => setEndYearPage(endYearPage - 12)} style={{ width: '34px', height: '34px', background: '#F3F6FB', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px' }}><FaChevronLeft /></button>
                  <span style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29', cursor: 'pointer' }} onClick={() => setEndView('days')}>{endYearPage} - {endYearPage + 11}</span>
                  <button type="button" onClick={() => setEndYearPage(endYearPage + 12)} style={{ width: '34px', height: '34px', background: 'transparent', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px' }}><FaChevronRight /></button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  {Array.from({ length: 12 }).map((_, i) => {
                    const y = endYearPage + i;
                    return (
                      <button key={y} type="button" onClick={() => { setEndDateObj(new Date(y, endMonth, endDateObj.getDate())); setEndYearInput(y); setEndView('days'); }} style={{ height: '42px', border: 'none', borderRadius: '10px', background: y === endYear ? '#06D6A0' : '#F3F6FB', color: y === endYear ? '#ffffff' : '#1B1B29', fontWeight: '600', fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s ease' }}>
                        {y}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={{ borderTop: '1px solid #F1F3F8', paddingTop: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ padding: '8px 16px', borderRadius: '10px', background: '#eff2f9', color: '#604BE8', fontWeight: '700', fontSize: '15px' }}>
              From: {formatDate(startDateObj)}
            </div>
            <span style={{ fontWeight: '700', color: '#94a3b8' }}>➔</span>
            <div style={{ padding: '8px 16px', borderRadius: '10px', background: '#ecfdf5', color: '#06D6A0', fontWeight: '700', fontSize: '15px' }}>
              To: {formatDate(endDateObj)}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={onCancel} style={{ height: '48px', padding: '0 24px', borderRadius: '12px', border: '1px solid #e2e8f0', background: '#F1F4F9', color: '#64748b', fontWeight: '700', fontSize: '15px', cursor: 'pointer', transition: 'all 0.2s' }}>Cancel</button>
            <button onClick={() => onSave(formatDate(startDateObj), formatDate(endDateObj))} style={{ height: '48px', padding: '0 32px', borderRadius: '12px', border: 'none', background: '#604BE8', color: '#ffffff', fontWeight: '700', fontSize: '15px', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 10px 20px -5px rgba(96, 75, 232, 0.4)' }}>Apply Date Range</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// SearchableSelect Component (Enhanced with Create New)

export default CustomDatePicker;
